
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useSucursal } from '../../../utils/sucursalContext';

interface Usuario {
  id: string;
  nombre: string;
  email: string;
  telefono: string;
  tipo_membresia: string;
  estado: string;
}

interface Plan {
  id: string;
  nombre: string;
  precio: string;
  duracion_dias: number;
  activo: boolean;
}

interface RegistrarPagoModalProps {
  onClose: () => void;
  onPagoRegistrado: () => void;
  metodosPagoDisponibles?: Array<{
    value: string;
    label: string;
  }>;
}

const METODOS_PAGO_FALLBACK: Array<{ value: string; label: string }> = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'tarjeta', label: 'Tarjeta' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'deposito', label: 'Deposito' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'otro', label: 'Otro' },
];

export default function RegistrarPagoModal({
  onClose,
  onPagoRegistrado,
  metodosPagoDisponibles,
}: RegistrarPagoModalProps) {
  const { sucursalActual } = useSucursal();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [planes, setPlanes] = useState<Plan[]>([]);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState<Usuario | null>(null);
  const [busquedaUsuario, setBusquedaUsuario] = useState('');
  const [mostrarListaUsuarios, setMostrarListaUsuarios] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const metodosPago = metodosPagoDisponibles ?? METODOS_PAGO_FALLBACK;

  const [formData, setFormData] = useState({
    plan: '',
    monto: '',
    metodo_pago: metodosPago[0]?.value || '',
    banco: '',
    numero_comprobante: '',
    fecha_pago: new Date().toISOString().split('T')[0],
    estado: 'Completado',
    notas: ''
  });

  const bancos = [
    'Banco de Chile',
    'Banco Estado',
    'Banco Santander',
    'Banco BCI',
    'Banco Scotiabank',
    'Banco Itaú',
    'Banco Security',
    'Banco Falabella',
    'Banco Ripley',
    'Banco Consorcio',
    'Banco BICE',
    'Banco Internacional',
    'Otro'
  ];

  useEffect(() => {
    cargarUsuarios();
    cargarPlanes();
  }, [sucursalActual]);

  useEffect(() => {
    if (!metodosPago.length) {
      setFormData((prev) => ({ ...prev, metodo_pago: '' }));
      return;
    }
    const metodoActualEsValido = metodosPago.some((metodo) => metodo.value === formData.metodo_pago);
    if (!metodoActualEsValido) {
      setFormData((prev) => ({ ...prev, metodo_pago: metodosPago[0].value }));
    }
  }, [metodosPago, formData.metodo_pago]);

  const cargarPlanes = async () => {
    try {
      const gimnasioId = sucursalActual?.gimnasio_id;
      if (!gimnasioId) {
        setPlanes([]);
        return;
      }

      const { data, error } = await supabase
        .from('planes')
        .select('id, nombre, precio, duracion_dias, activo')
        .eq('gimnasio_id', gimnasioId)
        .eq('activo', true)
        .order('precio');

      if (error) throw error;

      setPlanes(data || []);
    } catch (error) {
      console.error('Error al cargar planes:', error);
    }
  };

  const cargarUsuarios = async () => {
    if (!sucursalActual?.id) {
      setUsuarios([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const query = supabase
        .from('usuarios')
        .select('id, nombre, email, telefono, tipo_membresia, estado')
        .eq('sucursal_id', sucursalActual.id)
        .order('nombre');

      const { data, error } = await query;

      if (error) throw error;

      setUsuarios(data || []);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const usuariosFiltrados = usuarios.filter(usuario =>
    usuario.nombre.toLowerCase().includes(busquedaUsuario.toLowerCase()) ||
    usuario.email.toLowerCase().includes(busquedaUsuario.toLowerCase()) ||
    usuario.telefono.includes(busquedaUsuario)
  );

  const handleSeleccionarUsuario = (usuario: Usuario) => {
    setUsuarioSeleccionado(usuario);
    setBusquedaUsuario('');
    setMostrarListaUsuarios(false);

    // Autocompletar plan y monto según el tipo de membresía del usuario
    const planUsuario = planes.find(p => 
      p.nombre.toLowerCase().includes(usuario.tipo_membresia?.toLowerCase() || '')
    );

    if (planUsuario) {
      setFormData(prev => ({
        ...prev,
        plan: planUsuario.nombre,
        monto: planUsuario.precio
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        plan: usuario.tipo_membresia || '',
        monto: ''
      }));
    }
  };

  const handlePlanChange = (planNombre: string) => {
    const planSeleccionado = planes.find(p => p.nombre === planNombre);
    
    setFormData(prev => ({
      ...prev,
      plan: planNombre,
      monto: planSeleccionado ? planSeleccionado.precio : prev.monto
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!usuarioSeleccionado) {
      alert('Por favor selecciona un usuario');
      return;
    }

    if (!sucursalActual?.id) {
      alert('Debes seleccionar una sucursal activa para registrar pagos');
      return;
    }

    if (!formData.monto || Number(formData.monto) <= 0) {
      alert('Por favor ingresa un monto válido');
      return;
    }

    if (!metodosPago.some((metodo) => metodo.value === formData.metodo_pago)) {
      alert('No hay metodos de pago habilitados en Configuracion > Metodos de Pago.');
      return;
    }

    try {
      setGuardando(true);

      const pagoData = {
        usuario_id: usuarioSeleccionado.id,
        monto: Number(formData.monto),
        metodo_pago: formData.metodo_pago,
        concepto: `Pago de membresía - ${formData.plan}`,
        fecha_pago: formData.fecha_pago,
        estado: formData.estado.toLowerCase(),
        sucursal_id: sucursalActual.id,
        banco: formData.banco || null,
        numero_comprobante: formData.numero_comprobante || null,
        notas: formData.notas || null
      };

      const { error } = await supabase
        .from('pagos')
        .insert([pagoData]);

      if (error) throw error;

      onPagoRegistrado();
      onClose();
    } catch (error) {
      console.error('Error al registrar pago:', error);
      alert('Error al registrar el pago. Por favor intenta nuevamente.');
    } finally {
      setGuardando(false);
    }
  };

  const metodosRequierenBanco = ['transferencia', 'deposito', 'cheque'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Registrar Pago Manual</h2>
              <p className="text-sm text-gray-500 mt-1">Registra pagos por transferencia, efectivo u otros medios</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-8 py-6">
          <div className="space-y-5">
            {/* Selección de Usuario */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Usuario <span className="text-red-500">*</span>
              </label>
              
              {!usuarioSeleccionado ? (
                <div className="relative">
                  <div className="relative">
                    <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                    <input
                      type="text"
                      placeholder="Buscar por nombre, email o teléfono..."
                      value={busquedaUsuario}
                      onChange={(e) => {
                        setBusquedaUsuario(e.target.value);
                        setMostrarListaUsuarios(true);
                      }}
                      onFocus={() => setMostrarListaUsuarios(true)}
                      className="w-full pl-12 pr-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>

                  {mostrarListaUsuarios && busquedaUsuario && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      {loading ? (
                        <div className="p-6 text-center text-gray-500">
                          <i className="ri-loader-4-line animate-spin text-xl"></i>
                          <p className="mt-2 text-sm">Cargando usuarios...</p>
                        </div>
                      ) : usuariosFiltrados.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                          <i className="ri-user-search-line text-3xl mb-2"></i>
                          <p className="text-sm">No se encontraron usuarios</p>
                        </div>
                      ) : (
                        usuariosFiltrados.map((usuario) => (
                          <button
                            key={usuario.id}
                            type="button"
                            onClick={() => handleSeleccionarUsuario(usuario)}
                            className="w-full px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0 text-left transition-colors"
                          >
                            <div className="w-11 h-11 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                              <span className="text-sm font-bold text-white">
                                {usuario.nombre.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{usuario.nombre}</p>
                              <p className="text-xs text-gray-500 truncate">{usuario.email}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                usuario.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {usuario.estado}
                              </span>
                              <span className="text-xs text-gray-600 font-medium">{usuario.tipo_membresia}</span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-teal-50 to-teal-50/50 border-2 border-teal-200 rounded-xl">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-lg font-bold text-white">
                      {usuarioSeleccionado.nombre.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-lg">{usuarioSeleccionado.nombre}</p>
                    <p className="text-sm text-gray-600">{usuarioSeleccionado.email}</p>
                    <p className="text-sm text-teal-700 font-semibold mt-0.5">{usuarioSeleccionado.tipo_membresia}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUsuarioSeleccionado(null);
                      setFormData(prev => ({ ...prev, plan: '', monto: '' }));
                    }}
                    className="px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100 rounded-lg transition-colors"
                  >
                    Cambiar
                  </button>
                </div>
              )}
            </div>

            {/* Plan y Monto en dos columnas */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Plan <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.plan}
                  onChange={(e) => handlePlanChange(e.target.value)}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  required
                >
                  <option value="">Seleccionar plan</option>
                  {planes.map((plan) => (
                    <option key={plan.id} value={plan.nombre}>
                      {plan.nombre} - ${Number(plan.precio).toLocaleString('es-CL')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Monto <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                  <input
                    type="number"
                    value={formData.monto}
                    onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                    className="w-full pl-8 pr-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="65000"
                    min="0"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Método de Pago y Estado */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Método de Pago <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.metodo_pago}
                  onChange={(e) => setFormData({ ...formData, metodo_pago: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  disabled={metodosPago.length === 0}
                  required
                >
                  {metodosPago.length === 0 && (
                    <option value="">No hay métodos habilitados</option>
                  )}
                  {metodosPago.map((metodo) => (
                    <option key={metodo.value} value={metodo.value}>
                      {metodo.label}
                    </option>
                  ))}
                </select>
                {metodosPago.length === 0 && (
                  <p className="mt-2 text-xs text-amber-700">
                    Habilita al menos un método en Configuración &gt; Métodos de Pago.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estado del Pago
                </label>
                <select
                  value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  required
                >
                  <option value="Completado">Completado</option>
                  <option value="Pendiente">Pendiente de verificación</option>
                </select>
              </div>
            </div>

            {/* Banco (solo si aplica) */}
            {metodosRequierenBanco.includes(formData.metodo_pago) && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Banco
                </label>
                <select
                  value={formData.banco}
                  onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                >
                  <option value="">Seleccionar banco</option>
                  {bancos.map((banco) => (
                    <option key={banco} value={banco}>{banco}</option>
                  ))}
                </select>
              </div>
            )}

            {/* N° de Comprobante y Fecha */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  N° de Comprobante / Referencia
                </label>
                <input
                  type="text"
                  value={formData.numero_comprobante}
                  onChange={(e) => setFormData({ ...formData, numero_comprobante: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  placeholder="Ej: 123456789"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha del Pago <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.fecha_pago}
                  onChange={(e) => setFormData({ ...formData, fecha_pago: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>

            {/* Notas */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Notas / Observaciones
              </label>
              <textarea
                value={formData.notas}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, notas: e.target.value });
                  }
                }}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none transition-all"
                rows={3}
                maxLength={500}
                placeholder="Información adicional sobre el pago..."
              />
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-xs text-gray-500">
                  <i className="ri-information-line"></i> Registro de pago manual
                </p>
                <p className="text-xs text-gray-400">{formData.notas.length}/500</p>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex gap-3">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-teal-600 text-xl"></i>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-teal-900">Registro de pago manual</p>
                <p className="text-xs text-teal-700 mt-1">
                  Este registro es para pagos realizados fuera del sistema (transferencias, efectivo en recepción, etc.). El comprobante quedará registrado en el historial del usuario.
                </p>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 bg-gray-50 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-white hover:border-gray-400 transition-all font-semibold"
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={guardando || !usuarioSeleccionado || metodosPago.length === 0}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:from-teal-700 hover:to-teal-800 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-teal-500/30"
          >
            {guardando ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Guardando...
              </>
            ) : (
              <>
                <i className="ri-check-line text-lg"></i>
                Registrar Pago
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}




